import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaArrowRight, FaEye } from 'react-icons/fa';
import { SectionHeader, Card, Badge, AnimatedSection, StaggerContainer, StaggerItem } from '../components/common';
import { blogsApi } from '../services/api';

// Calculate read time from content
const calculateReadTime = (content) => {
  if (!content) return '3 min read';
  const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200); // Average reading speed
  return `${minutes} min read`;
};

const Blogs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  const fetchCategories = useCallback(async () => {
    try {
      const response = await blogsApi.getCategories();
      if (response.success && response.data?.categories) {
        setCategories(response.data.categories);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Sync URL params with state
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl && categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams, selectedCategory]);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogsApi.getPublished(pagination.page, 12, selectedCategory);
      if (response.success && response.data) {
        // Transform API response to match frontend expectations
        const transformedBlogs = response.data.map(blog => ({
          id: blog.id,
          title: blog.title,
          slug: blog.slug,
          excerpt: blog.excerpt || '',
          createdAt: blog.published_at || blog.created_at,
          readTime: calculateReadTime(blog.content),
          featuredImage: blog.featured_image,
          views: blog.views,
          authorName: blog.author_name,
          category: blog.category,
          categoryName: blog.category_name
        }));
        setBlogPosts(transformedBlogs);
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            totalPages: response.pagination.total_pages
          }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, selectedCategory]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setPagination(prev => ({ ...prev, page: 1 }));
    // Update URL params
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <>
      <Helmet>
        <title>Blogs | Rakesh Kumar Tripathi</title>
        <meta name="description" content="Read insights on cyber security, IT infrastructure, ERPNext, cloud computing, and enterprise technology from Rakesh Kumar Tripathi." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-8 pb-16 gradient-bg-subtle">
        <div className="container-custom">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto">
              <Badge variant="primary" className="mb-4">Blog</Badge>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-dark-800 mb-6">
                Insights & Articles
              </h1>
              <p className="text-lg text-dark-500">
                Sharing knowledge and experiences in cyber security, IT infrastructure,
                and enterprise technology solutions.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>


      {/* Category Filter */}
      <section className="py-6 bg-white border-b">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => handleCategoryChange('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-100 text-dark-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-dark-600 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <StaggerItem key={post.id}>
                  <Link to={`/blogs/${post.slug}`}>
                    <Card elevated className="h-full flex flex-col overflow-hidden group cursor-pointer">
                      {/* Image Placeholder */}
                      <div className="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                        {post.featuredImage ? (
                          <img
                            src={post.featuredImage}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-4xl font-bold text-primary-300">
                              {post.title.charAt(0)}
                            </span>
                          </div>
                        )}
                        {post.views > 0 && (
                          <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded text-xs flex items-center">
                            <FaEye className="w-3 h-3 mr-1" />
                            {post.views}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6 flex-1 flex flex-col">
                        {post.categoryName && (
                          <span className="inline-block w-fit px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-2">
                            {post.categoryName}
                          </span>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-dark-400 mb-3">
                          <span className="flex items-center">
                            <FaCalendarAlt className="w-3 h-3 mr-1" />
                            {formatDate(post.createdAt)}
                          </span>
                          <span className="flex items-center">
                            <FaClock className="w-3 h-3 mr-1" />
                            {post.readTime}
                          </span>
                        </div>

                        <h3 className="text-lg font-bold text-dark-800 mb-3 group-hover:text-primary-500 transition-colors">
                          {post.title}
                        </h3>

                        <p className="text-dark-500 text-sm mb-4 flex-1 line-clamp-3 text-justify">
                          {post.excerpt}
                        </p>

                        <motion.span
                          className="inline-flex items-center text-primary-500 font-medium text-sm group/btn"
                          whileHover={{ x: 5 }}
                        >
                          Read More
                          <FaArrowRight className="ml-2 w-3 h-3 transition-transform group-hover/btn:translate-x-1" />
                        </motion.span>
                      </div>
                    </Card>
                  </Link>
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}

          {!loading && blogPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-dark-500">No blog posts available yet.</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center space-x-4 mt-12">
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                Previous
              </button>
              <span className="text-dark-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section-padding bg-dark-800">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <SectionHeader
              badge="Stay Updated"
              title="Subscribe to Newsletter"
              subtitle="Get the latest insights on cyber security, IT infrastructure, and technology trends delivered to your inbox."
              titleClassName="text-white"
              subtitleClassName="text-gray-400"
            />

            <motion.div
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg bg-dark-700 text-white placeholder-gray-400 border border-dark-600 focus:border-primary-500 focus:outline-none"
              />
              <button className="px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors">
                Subscribe
              </button>
            </motion.div>

            <p className="text-gray-500 text-sm mt-4">
              No spam, unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

    </>
  );
};

export default Blogs;
